/**
 * Resource Hook Factory
 * 
 * Creates a standard set of TanStack Query hooks for any React-Admin resource.
 * This provides a consistent API for all CRUD operations across the application.
 * 
 * @example
 * ```typescript
 * // Define your hooks for a resource
 * const noteHooks = createResourceHooks<Note>('conversation_notes', noteKeys)
 * 
 * // Export individual hooks
 * export const { useList, useOne, useMany, useCreate, useUpdate, useDelete } = noteHooks
 * 
 * // Use in components
 * const { data, isLoading } = useList({ page: 1, perPage: 25 })
 * const { data: note } = useOne(noteId)
 * const createMutation = useCreate()
 * await createMutation.mutateAsync({ content: 'New note' })
 * ```
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { raClient, type RAListParams, type RAListResponse } from '@/lib/raClient'
import type { QueryKeyFactory } from '@/lib/queryKeys'

// ============================================================================
// Types
// ============================================================================

export interface ResourceHooks<T> {
  /**
   * Hook for fetching a paginated list of records
   */
  useList: (
    params?: RAListParams,
    options?: { enabled?: boolean; staleTime?: number }
  ) => ReturnType<typeof useQuery<RAListResponse<T>>>

  /**
   * Hook for fetching a single record by ID
   */
  useOne: (
    id: string | number | undefined,
    options?: { enabled?: boolean; staleTime?: number }
  ) => ReturnType<typeof useQuery<T>>

  /**
   * Hook for fetching multiple records by IDs
   */
  useMany: (
    ids: (string | number)[],
    options?: { enabled?: boolean; staleTime?: number }
  ) => ReturnType<typeof useQuery<T[]>>

  /**
   * Hook for creating a new record
   */
  useCreate: () => ReturnType<typeof useMutation<T, Error, Partial<T>>>

  /**
   * Hook for updating an existing record
   */
  useUpdate: () => ReturnType<
    typeof useMutation<T, Error, { id: string | number; data: Partial<T> }>
  >

  /**
   * Hook for deleting a record
   */
  useDelete: () => ReturnType<typeof useMutation<void, Error, string | number>>

  /**
   * The query key factory for this resource
   */
  keys: QueryKeyFactory
}

// ============================================================================
// Factory Function
// ============================================================================

/**
 * Create a standard set of hooks for a React-Admin resource
 * 
 * @param resource - The resource name (e.g., 'conversations', 'assistants')
 * @param keys - The query key factory for this resource
 * @returns An object containing all CRUD hooks for the resource
 * 
 * @example
 * ```typescript
 * // In hooks/api/useNotes.ts
 * import { createResourceHooks } from './createResourceHooks'
 * import { createQueryKeys } from '@/lib/queryKeys'
 * 
 * interface Note {
 *   id: number
 *   content: string
 *   status: 'open' | 'closed'
 *   // ...
 * }
 * 
 * export const noteKeys = createQueryKeys('conversation_notes')
 * 
 * const noteHooks = createResourceHooks<Note>('conversation_notes', noteKeys)
 * 
 * export const useNotes = noteHooks.useList
 * export const useNote = noteHooks.useOne
 * export const useNotesByIds = noteHooks.useMany
 * export const useCreateNote = noteHooks.useCreate
 * export const useUpdateNote = noteHooks.useUpdate
 * export const useDeleteNote = noteHooks.useDelete
 * ```
 */
export function createResourceHooks<T>(
  resource: string,
  keys: QueryKeyFactory
): ResourceHooks<T> {
  return {
    keys,

    useList(params = {}, options = {}) {
      return useQuery({
        queryKey: keys.list(params),
        queryFn: () => raClient.getList<T>(resource, params),
        enabled: options.enabled ?? true,
        staleTime: options.staleTime ?? 30 * 1000, // 30 seconds default
      })
    },

    useOne(id, options = {}) {
      return useQuery({
        queryKey: keys.detail(id ?? ''),
        queryFn: () => raClient.getOne<T>(resource, id!),
        enabled: (options.enabled ?? true) && !!id,
        staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutes default
      })
    },

    useMany(ids, options = {}) {
      return useQuery({
        queryKey: keys.byIds(ids),
        queryFn: () => raClient.getMany<T>(resource, ids),
        enabled: (options.enabled ?? true) && ids.length > 0,
        staleTime: options.staleTime ?? 5 * 60 * 1000, // 5 minutes default
      })
    },

    useCreate() {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: (data: Partial<T>) => raClient.create<T>(resource, data),
        onSuccess: () => {
          // Invalidate all lists for this resource
          queryClient.invalidateQueries({ queryKey: keys.lists() })
        },
      })
    },

    useUpdate() {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: ({ id, data }: { id: string | number; data: Partial<T> }) =>
          raClient.update<T>(resource, id, data),
        onSuccess: (_, { id }) => {
          // Invalidate the specific detail and all lists
          queryClient.invalidateQueries({ queryKey: keys.detail(id) })
          queryClient.invalidateQueries({ queryKey: keys.lists() })
        },
      })
    },

    useDelete() {
      const queryClient = useQueryClient()

      return useMutation({
        mutationFn: (id: string | number) => raClient.delete(resource, id),
        onSuccess: () => {
          // Invalidate all queries for this resource
          queryClient.invalidateQueries({ queryKey: keys.all })
        },
      })
    },
  }
}
